import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BorderGlow } from '../../components/BorderGlow';
import { Button } from '../../components/Button';
import { ProgressBar } from '../../components/ProgressBar';
import { useSidebar } from '../../components/Sidebar';
import { Icon } from '../../icons/Icon';
import { useUpdateContext } from '../../hooks/useUpdateContext';
import { usePlatform } from '../../hooks/usePlatform';
import { hexToHsl, useThemeGlowColors } from '../../utils/themeGlow';
import {
  resolveSidebarUpdateNav,
  type SidebarUpdateNavLabel,
} from './sidebarUpdateNavState';

const LABEL_I18N: Record<SidebarUpdateNavLabel, string> = {
  available: 'sidebar.update.available',
  downloading: 'sidebar.update.downloading',
  install: 'sidebar.update.install',
  refreshWeb: 'sidebar.update.refreshWeb',
  error: 'sidebar.update.error',
};

/** Must exceed the ~4s BorderGlow sweep so sweeps never overlap. */
const SWEEP_INTERVAL_MS = 12_000;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Compact sidebar entry for app updates: navigates to Check for Updates.
 * Shown above the identity / alias block when an update needs attention.
 */
export function SidebarUpdateNav() {
  const { t } = useTranslation();
  const platform = usePlatform();
  const navigate = useNavigate();
  const { closeMobile } = useSidebar();
  const { status, downloadProgress, installing, applyUpdate } = useUpdateContext();
  const theme = useThemeGlowColors();
  const [sweepKey, setSweepKey] = useState(0);

  const resolved = resolveSidebarUpdateNav(
    status,
    platform,
    installing,
    downloadProgress,
  );

  useEffect(() => {
    if (!resolved.visible || prefersReducedMotion()) return;
    const id = setInterval(() => setSweepKey((k) => k + 1), SWEEP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [resolved.visible]);

  if (!resolved.visible) return null;

  const primaryLabel = t(LABEL_I18N[resolved.label]);
  const showProgress = resolved.label === 'downloading';
  const percent = resolved.progressPercent ?? 0;

  const handleClick = () => {
    closeMobile();
    if (platform === 'web' && resolved.label === 'refreshWeb') {
      applyUpdate();
      return;
    }
    navigate('/about/updates');
  };

  return (
    <div className="sidebar-update-nav-row">
      <BorderGlow
        className="sidebar-update-nav-glow"
        animated={!prefersReducedMotion()}
        sweepKey={sweepKey}
        colors={[theme.primary, theme.secondary, theme.primary]}
        glowColor={hexToHsl(theme.primary)}
        backgroundColor="var(--color-bg-secondary)"
        borderRadius={8}
        glowRadius={10}
        glowIntensity={0.8}
        fillOpacity={0.35}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="sidebar-update-nav-btn"
          onClick={handleClick}
          aria-label={primaryLabel}
        >
          <Icon name="download" />
          <span className="sidebar-update-nav-label">{primaryLabel}</span>
        </Button>
      </BorderGlow>
      {showProgress && (
        <div className="sidebar-update-nav-progress">
          <ProgressBar percent={percent} />
        </div>
      )}
    </div>
  );
}
